package fr.frux.core.common.logging

enum class LogLevel { DEBUG, INFO, ERROR }

/**
 * Simple multiplatform logger.
 * Uses platform-specific logging (Logcat on Android, NSLog on iOS, println elsewhere).
 *
 * Le niveau minimal est injecté au démarrage par le point d'entrée de plateforme (#175) :
 * une release ne doit rien écrire sous ERROR. [errorSink] permet d'acheminer les erreurs
 * vers un crash reporter sans que ce module dépende du SDK concerné.
 */
object Logger {
    var minLevel: LogLevel = LogLevel.DEBUG

    /** Reçoit chaque appel à [e], indépendamment de [minLevel] (crash reporting en release). */
    var errorSink: ((tag: String, message: String, throwable: Throwable?) -> Unit)? = null

    internal var writeLog: (LogLevel, String, String, Throwable?) -> Unit = ::platformWriteLog

    fun d(
        tag: String,
        message: String,
    ) = log(LogLevel.DEBUG, tag, message, null)

    fun i(
        tag: String,
        message: String,
    ) = log(LogLevel.INFO, tag, message, null)

    fun e(
        tag: String,
        message: String,
        throwable: Throwable? = null,
    ) {
        log(LogLevel.ERROR, tag, message, throwable)
        errorSink?.invoke(tag, message, throwable)
    }

    private fun log(
        level: LogLevel,
        tag: String,
        message: String,
        throwable: Throwable?,
    ) {
        if (level >= minLevel) writeLog(level, tag, message, throwable)
    }
}

internal expect fun platformWriteLog(
    level: LogLevel,
    tag: String,
    message: String,
    throwable: Throwable?,
)
